import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Form, FormGroup, Label, Input } from "reactstrap";
import PageHeader from "../../../../components/PageHeader";
import Loading from "../../../../components/utils/Loading";
import ClientLogo from "../../client/ClientLogo";
import ClientLogoUpload from "../../client/ClientLogoUpload";
import ClientHistoryTab from "../../client/ClientHistoryTab";
import {
  buildClientLogoBlobName,
  readFileAsBase64,
} from "../../client/clientUtils";
import {
  deleteClient,
  getClient,
  updateClient,
} from "../../../../services/client";
import { getCorporationData } from "../../../../services/corporation";
import DeleteModal from "../../../../components/modals/DeleteModal";

const PANEL_CARD =
  "w-full max-w-[640px] overflow-hidden rounded-lg border border-[#d3d3d3] bg-white pt-6 px-8 pb-8";
const CLIENT_INPUT_CLASS =
  "h-10 border border-[#d3d3d3] rounded px-3 text-[14px] leading-[22px] text-[#222222] outline-none focus:border-[#00A7A6] focus:shadow-none";

const SettingsClientEditor = ({ token }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState("general");
  const [corporationId, setCorporationId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [name, setName] = useState("");
  const [clientUniqueId, setClientUniqueId] = useState("");
  const [address, setAddress] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const [logoFileSize, setLogoFileSize] = useState(0);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const tabs = useMemo(
    () => [
      { key: "general", label: t("settingsClients.tabGeneral") },
      { key: "history", label: t("settingsClients.tabHistory") },
    ],
    [t],
  );

  const headerTabs = tabs.map((item) => ({ id: item.key, label: item.label }));

  const loadCorporation = useCallback(async () => {
    try {
      const res = await getCorporationData();
      setCorporationId(res?.data?.corporation?._id ?? null);
    } catch {
      setCorporationId(null);
    }
  }, []);

  const loadClient = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getClient(id);
      const client = res?.data?.client;
      if (!client) throw new Error("not found");
      const loadedName = (client.name || "").trim();
      const loadedRef = (client.clientUniqueId || "").trim();
      const loadedAddress = (client.address || "").trim();
      const loadedLogoUrl = client.logoUrl || "";

      setName(loadedName);
      setClientUniqueId(loadedRef);
      setAddress(loadedAddress);
      setLogoUrl(loadedLogoUrl);
      setLogoPreviewUrl("");
      setLogoFile(null);
      setInitialSnapshot({
        name: loadedName,
        clientUniqueId: loadedRef,
        address: loadedAddress,
        logoUrl: loadedLogoUrl,
      });
      if (client.logoUrl) {
        const parts = String(client.logoUrl).split("/");
        setLogoFileName(parts[parts.length - 1] || "");
        setLogoFileSize(0);
      } else {
        setLogoFileName("");
        setLogoFileSize(0);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.loadError"));
      history.push("/settings/clients");
    } finally {
      setIsLoading(false);
    }
  }, [history, id, t]);

  useEffect(() => {
    if (!token) return;
    loadCorporation();
    loadClient();
  }, [token, loadCorporation, loadClient]);

  const handleLogoRemove = () => {
    if (logoPreviewUrl) {
      setLogoPreviewUrl("");
      setLogoFile(null);
      if (logoUrl) {
        const parts = String(logoUrl).split("/");
        setLogoFileName(parts[parts.length - 1] || "");
        setLogoFileSize(0);
      } else {
        setLogoFileName("");
        setLogoFileSize(0);
      }
      return;
    }
    setLogoUrl("");
    setLogoFile(null);
    setLogoFileName("");
    setLogoFileSize(0);
  };

  const handleLogoSelect = async (file) => {
    setLogoFileName(file.name);
    setLogoFileSize(file.size);
    try {
      const base64File = await readFileAsBase64(file);
      setLogoPreviewUrl(base64File);
      setLogoFile({ base64File });
    } catch (error) {
      console.error(error);
      handleLogoRemove();
      toast.error(t("Failed to upload file"));
    }
  };

  const buildPayload = () => {
    const payload = {
      name: name.trim(),
      address: address.trim(),
      clientUniqueId: clientUniqueId.trim(),
      logoUrl: logoUrl || "",
    };
    if (logoFile) {
      const corpId = corporationId || "unknown";
      const blobName = buildClientLogoBlobName(corpId, id, logoFileName);
      payload.logoUrl = `https://cameraanalysis.blob.core.windows.net/ak-corporation/${blobName}`;
      payload.logoFile = { blobName, base64File: logoFile.base64File };
    }
    return payload;
  };

  const hasLogoPreview = Boolean(logoPreviewUrl || logoUrl);

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) return false;
    const logoChanged =
      Boolean(logoFile) || logoUrl !== initialSnapshot.logoUrl;
    return (
      name.trim() !== initialSnapshot.name ||
      clientUniqueId.trim() !== initialSnapshot.clientUniqueId ||
      address.trim() !== initialSnapshot.address ||
      logoChanged
    );
  }, [name, clientUniqueId, address, logoUrl, logoFile, initialSnapshot]);

  const showFooter = tab === "general" && hasUnsavedChanges;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t("name_field_required"));
      return;
    }

    setIsSaving(true);
    try {
      await updateClient(id, {
        dataLineUpdate: buildPayload(),
      });
      toast.success(t("settingsClients.clientUpdated"));
      history.push("/settings/clients");
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteClient(id);
      toast.success(t("settingsClients.clientDeleted"));
      history.push("/settings/clients");
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.saveError"));
    }
  };

  const actionsMenuItems = [
    {
      label: t("settingsClients.deleteClient"),
      icon: <i className="far fa-trash-alt text-[#222222]" aria-hidden />,
      onClick: () => setShowDeleteModal(true),
    },
  ];

  if (!token) return null;
  if (isLoading) return <Loading color="#F5F5F5" />;

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={name || "—"}
        showBackButton
        backRoute="/settings/clients"
        backLabel={t("settingsClients.backToList")}
        tabs={headerTabs}
        activeTab={tab}
        onTabChange={setTab}
        actionsMenuItems={actionsMenuItems}
      />

      <div
        className={`settings-content-wrapper ${showFooter ? "pb-28" : "pb-6"}`}
      >
        <div className="flex justify-center">
          {tab === "general" ? (
            <div className={PANEL_CARD}>
              <Form
                className="flex flex-col gap-[26px]"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <ClientLogo
                  client={{ name, logoUrl: logoPreviewUrl ? "" : logoUrl }}
                  previewSrc={logoPreviewUrl || null}
                  size={84}
                  className="!rounded-lg"
                  usePlaceholder={!hasLogoPreview}
                />
                <FormGroup className="mb-0">
                  <Label for="client-name" className="font-semibold">
                    {t("settingsClients.fieldName")}{" "}
                    <span className="text-[#AF2F3C]">*</span>
                  </Label>
                  <Input
                    id="client-name"
                    className={CLIENT_INPUT_CLASS}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label for="client-ref" className="font-semibold">
                    {t("settingsClients.fieldReference")}
                  </Label>
                  <Input
                    id="client-ref"
                    className={CLIENT_INPUT_CLASS}
                    value={clientUniqueId}
                    onChange={(e) => setClientUniqueId(e.target.value)}
                  />
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label for="client-address" className="font-semibold">
                    {t("settingsClients.fieldAddress")}
                  </Label>
                  <Input
                    id="client-address"
                    className={CLIENT_INPUT_CLASS}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </FormGroup>
                <ClientLogoUpload
                  fileName={logoFileName}
                  fileSize={logoFileSize}
                  isComplete={Boolean(logoFile || logoUrl)}
                  onSelectFile={handleLogoSelect}
                  onRemove={handleLogoRemove}
                  emptyTrigger="pill"
                />
              </Form>
            </div>
          ) : (
            <ClientHistoryTab clientId={id} />
          )}
        </div>
      </div>

      {showFooter && (
        <div className="sticky bottom-0 z-[2001] w-full bg-white border-t border-[#d3d3d3]">
          <div className="mx-auto max-w-[640px] py-[22px] px-8 flex justify-end items-center gap-2">
            <button
              type="button"
              className="modal-button modal-button-cancel"
              onClick={() => history.push("/settings/clients")}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="modal-button modal-button-save"
              disabled={isSaving}
              onClick={handleSave}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          modal={showDeleteModal}
          setModal={setShowDeleteModal}
          id={id}
          message1={t("settingsClients.deleteConfirm", { name })}
          message2={name}
          messageValidation={t("settingsClients.clientDeleted")}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default SettingsClientEditor;
